import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData } from "../SymbolData";
import { FEATURES_CONFIG } from "../featuresConfig";
import { Settings } from "../settings";

// ── The settled window a composite is captured inside ──
//
// Two symbols in this game open a door and keep it open with something
// standing in it. Both are captured the same way, and the only difference
// between them is the skeleton: the slot and the pose have the same names on
// both rigs, and both names are the ones the LIVE reveal uses — see each
// config's `slotNames` and `sequence.settle`.
// Ceiling on the copies drawn per blurred symbol (see _createBlurredTexture).
//
// The target is one per pixel of travel, which is where the copies overlap and
// the smear turns continuous. High enough that the cap does not bite on any
// symbol this game ships: at 48 the largest ones were down to a copy every
// 1.8px, which is where a faint ghosting can creep back in.
//
// It costs nothing to be generous. They share one texture and one blend mode,
// so PIXI batches the lot into a SINGLE draw call, and this runs once while
// the intro popup is up.
const MAX_SMEAR_SAMPLES = 96;

// Free room the extractor already leaves around every symbol.
//
// Both extraction paths size their canvas as `ceil(halfExtent * 2) + 16` and
// centre the symbol in it, so the content is never taller than this much less
// than the texture. The smear gets that 16px for nothing — only what it needs
// BEYOND it has to be paid for by squashing the symbol.
const EXTRACT_PADDING_PX = 16;

// How far the symbol may be squashed to make room for its own smear. Only a
// deliberately extreme SMEAR_RATIO can reach this; it exists so a bad value
// crushes nothing — the smear is capped to fit instead.
const MIN_SMEAR_SCALE_Y = 0.4;

const REVEAL_HOST_SLOT = "attachment_slot_symbol";
// ── `win`, NOT `revealed_static` — and the doors are the whole reason ──
//
// A capture has no history. `setAnimation(pose)` then `state.apply()` gives the
// clip's frame 0 over the SETUP pose, so any slot the clip does not key falls
// back to setup — and in `scg` the setup pose has both doors attached:
//
//   revealed_static  keys 2 slots: eclipse2, scatter1_bg.
//                    mystery_door_l / mystery_door_r are NOT among them.
//   win              keys 5, including both doors, each to an EMPTY attachment
//                    at time 0 — which is what takes them off.
//
// So capturing at `revealed_static` froze an OPEN window behind SHUT doors, and
// that is the picture a player came back to after the bonus: the gold door,
// with the package they won hidden underneath it. Reported by Vladimir
// 2026-09-11, survived two attempts at a fix, and found 2026-09-23 only after a
// probe proved the texture was on the sprite at alpha 1 with nothing over it —
// so the capture itself had to be wrong. His words: *"are we freezing wrong
// animation?"* Yes.
//
// `revealed_static` is still correct for the LIVE cell, and every other use of
// it is live: there the `reveal` clip has just played and taken the doors off,
// so holding it afterwards keeps them off. Only a cold capture needs the clip
// that states it.
//
// THE SAME FIX, one rig earlier: GOLDEN_HOST below is posed at `win` for `myg`
// for exactly this reason — see its note on frame zero. `scg` had the
// `revealed_static` clip that `myg` lacks, which is why nobody looked twice.
//
// `win` keys a SUPERSET of `revealed_static` (both of its slots, plus the doors
// and the frame), and at time 0 everything it adds is alpha 0 or detached, so
// nothing is gained that should not be there. Checked against the file:
// H1_energy_01 has no setup attachment, and the outer frame and backing are
// keyed by neither clip and stay as they are.
const REVEAL_HOST_POSE = "win";

/**
 * Rigs whose flat texture must be the SETUP pose, never a clip.
 *
 * See the note in _extractSingleFrame. Add a rig here when its first
 * ANIM_PRIORITY clip hides art that the resting picture needs — it is the
 * durable form of shipping an empty `static` animation, and unlike that it
 * survives a re-export.
 */
const SETUP_POSE_SPINES = new Set(["gamble_cards"]);

/** The gamble scatter: the gold window with the awarded package inside it. */
const GAMBLE_HOST = { spine: "scg", slot: REVEAL_HOST_SLOT, pose: REVEAL_HOST_POSE };
/**
 * The GOLDEN MYSTERY's host: the lit window with a strip cell inside it.
 *
 * `myg`, the same rig GOLDEN_FRAME_SYMBOL_ID pools. It used to be the old `my`,
 * borrowed because `myg` had no frame of its own; it has one now and `my` is
 * gone.
 *
 * ── Posed at `win`, and the reason is FRAME ZERO ──
 *
 * The capture below does `setAnimation(pose)` then `state.update(0)`, so it
 * takes the clip's FIRST frame, not its last. For the old `my` that was
 * invisible: `revealed_static` is a held pose, so frame 0 is the whole clip.
 *
 * `myg` has no such clip, and the obvious substitute is wrong in both
 * directions:
 *
 *   `reveal`  frame 0 is the CLOSED cell — gold sign up, slot at alpha 0.
 *             That is the state the lid opens ONTO, not the settled one, so
 *             parking a finished cell on it puts the sign back over the
 *             outcome the player just won.
 *   `static`  keys nothing at all, so the skeleton falls back to setup and the
 *             gold sign returns for the same reason.
 *
 * `win` keys, at time 0, exactly the settled state: every piece of the gold
 * sign at alpha 0, the boom and energy attachments cleared,
 * `attachment_slot_symbol` opaque, the bounce bone at rest and the bamboo
 * lights left at setup — lit. The lit window with the landed symbol in it,
 * which is precisely what this composite is for.
 */
const MYSTERY_HOST = { spine: "myg", slot: REVEAL_HOST_SLOT, pose: "win" };

/**
 * SpineTextureExtractor
 *
 * Replaces traditional spritesheets by extracting static frames from symbol
 * spine animations at load time. Produces PIXI.Texture objects (normal and
 * blurred variants) used during reel spinning.
 *
 * How it works:
 *   1. For each symbol in symbolData that has a spine file, instantiate a
 *      temporary Spine, select the best animation (idle → loop → win →
 *      first available), and render frame 0 into a RenderTexture —
 *      this becomes the reel symbol texture.
 *   2. Optionally, post-process each texture with a vertical squash + blur
 *      to generate blurred variants for the spin motion effect.
 *
 * Key design decisions:
 *   - Textures are origin-centered: the spine's (0,0) maps to the texture
 *     center, so sprite.anchor(0.5) aligns perfectly with the live spine
 *     overlay during win animations (no visual "bounce" on transition).
 *   - Animation selection is automatic via priority fallback — no specific
 *     animation name is required in the spine file.
 *   - All results are cached — subsequent calls return instantly.
 *
 * Output format (drop-in replacement for PIXI.Spritesheet):
 *   { textures: { "w1.png": Texture, "h1.png": Texture, ... } }
 *   { textures: { "w1_blurred.png": Texture, ... } }   // blurred variant
 */
class SpineTextureExtractor {
  // Module-level singleton — initialized once, consumed everywhere
  static _instance = null;

  /**
   * One-time initialization. Call once when renderer is available.
   * Extracts all symbol textures, blurred variants, and generates fonts.
   */
  static initialize(renderer) {
    if (SpineTextureExtractor._instance) return;

    const extractor = new SpineTextureExtractor(renderer);
    extractor.extractAll();

    if (FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION?.ENABLED) {
      extractor.extractAllBlurred();
    }

    SpineTextureExtractor._instance = extractor;
  }

  /** @returns {{ textures: Record<string, PIXI.RenderTexture> } | null} */
  static getSymbols() {
    return SpineTextureExtractor._instance?.extractAll() ?? null;
  }

  /** @returns {{ textures: Record<string, PIXI.RenderTexture> } | null} */
  static getBlurredSymbols() {
    if (!FEATURES_CONFIG.SPIN.BLURRED_TEXTURES_ENABLED) return null;
    return SpineTextureExtractor._instance?.extractAllBlurred() ?? null;
  }

  /** @returns {Record<string, { src: string, width: number, height: number }> | null} */
  static getDataURLs() {
    return SpineTextureExtractor._instance?.extractAllAsDataURLs() ?? null;
  }

  /**
   * A symbol's picture as a data URL, TRIMMED to its visible pixels (plus a
   * few of margin) — for the paytable, where the extraction frame's empty
   * border (it spans every attachment's bounds, glows and effects included)
   * shrank the symbol inside a mostly transparent box (Vladimir, 2026-09-30:
   * "images are way too small"). Cached per texture name; null when unknown.
   * @param {string} name Texture name (SymbolData `texture`, e.g. "w0", "sc").
   * @returns {string|null}
   */
  static getTrimmedDataURL(name) {
    const self = SpineTextureExtractor._instance;
    if (!self) return null;
    if (!self._trimmedDataURLCache) self._trimmedDataURLCache = {};
    if (self._trimmedDataURLCache[name] !== undefined) {
      return self._trimmedDataURLCache[name];
    }
    let url = null;
    try {
      const texture = self.extractAll()?.textures?.[`${name}.png`];
      const trimmed = texture ? self._trimmedCanvas(texture) : null;
      url = trimmed ? trimmed.toDataURL("image/png") : null;
    } catch {
      url = null;
    }
    self._trimmedDataURLCache[name] = url;
    return url;
  }

  /**
   * A symbol's picture as a PIXI texture, TRIMMED to its visible pixels — the
   * getTrimmedDataURL twin for PIXI-drawn UI (the Bonus Buy cards: a coin
   * standing in for the Hold & Win art, 2026-09-30). Cached; null when unknown.
   * @param {string} name Texture name (SymbolData `texture`, e.g. "c").
   * @returns {PIXI.Texture|null}
   */
  static getTrimmedTexture(name) {
    const self = SpineTextureExtractor._instance;
    if (!self) return null;
    if (!self._trimmedTextureCache) self._trimmedTextureCache = {};
    if (self._trimmedTextureCache[name] !== undefined) {
      return self._trimmedTextureCache[name];
    }
    let texture = null;
    try {
      const source = self.extractAll()?.textures?.[`${name}.png`];
      const trimmed = source ? self._trimmedCanvas(source) : null;
      texture = trimmed ? PIXI.Texture.from(trimmed) : null;
    } catch {
      texture = null;
    }
    self._trimmedTextureCache[name] = texture;
    return texture;
  }

  /**
   * A texture as a canvas cut to its visible pixels (alpha > 8) plus `pad`.
   * Null when the texture is empty.
   */
  // `alpha`: the cut-off — 8 keeps the faintest glow; a high one (128) finds
  // the visible body (see getJackpotCoinsCompositeDataURL).
  _trimmedCanvas(texture, pad = 4, alpha = 8) {
    const source = this.renderer.extract.canvas(texture);
    if (!source) return null;
    const { width, height } = source;
    const data = source.getContext("2d").getImageData(0, 0, width, height).data;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > alpha) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX || maxY < minY) return null;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad);
    maxY = Math.min(height - 1, maxY + pad);
    const out = document.createElement("canvas");
    out.width = maxX - minX + 1;
    out.height = maxY - minY + 1;
    out
      .getContext("2d")
      .drawImage(source, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  /**
   * The paytable's JACKPOT SYMBOLS picture: the five jackpot coins (`cj`,
   * skins Jp_*) joined into one group, as the design draws it (Vladimir,
   * 2026-09-30) — Major left, Grand in the middle, larger and raised, Midi
   * right, Minor and Mini in front below. Positions in coin widths from the
   * group's centre; drawn back to front. Cached; null when the rig is missing.
   * @returns {string|null}
   */
  static getJackpotCoinsCompositeDataURL() {
    const self = SpineTextureExtractor._instance;
    if (!self) return null;
    if (self._jackpotCompositeURL !== undefined) return self._jackpotCompositeURL;
    let url = null;
    try {
      // [skin, x, y, size] — back to front. Measured off the design
      // (Vladimir, 2026-09-30): the unit is one coin (Major), x / y are the
      // coin's centre from Grand's, `size` its diameter in units.
      // const LAYOUT = [
      //   ["Jp_major", -0.95, 0, 1],
      //   ["Jp_midi", 0.95, 0, 1],
      //   ["Jp_grand", 0, -0.22, 1.2],
      //   ["Jp_minor", -0.5, 0.72, 0.9],
      //   ["Jp_mini", 0.5, 0.72, 0.9],
      // ];
      // const LAYOUT = [
      //   ["Jp_major", -1.09, 0.23, 1],
      //   ["Jp_midi", 1.1, 0.23, 1],
      //   ["Jp_grand", 0, 0, 1.17],
      //   ["Jp_minor", -0.46, 1.0, 0.87],
      //   ["Jp_mini", 0.47, 1.0, 0.87],
      // ];
      // Re-measured off the design on the VISIBLE coins (2026-09-30): the unit
      // was the trimmed picture, faint outer glow included — ~20% wider than
      // the coin the eye sees — so every distance came out ~20% too far apart.
      const JACKPOT_COIN_SIZE_BOOST = 1.15;
      const LAYOUT = [
        ["Jp_major", -1.12, 0.31, 1],
        ["Jp_midi", 1.12, 0.31, 1],
        ["Jp_grand", 0, 0, 1.22],
        ["Jp_minor", -0.47, 1.1, 0.87],
        ["Jp_mini", 0.47, 1.1, 0.87],
      ];
      const coins = LAYOUT.map(([skin, x, y, scale]) => {
        const texture = SpineTextureExtractor.getSkinTexture("cj", skin);
        return {
          // the whole picture (glow included) is what is drawn…
          canvas: texture ? self._trimmedCanvas(texture, 2) : null,
          // …the visible body (alpha > 128) is what is sized and placed
          body: texture ? self._trimmedCanvas(texture, 0, 128) : null,
          x,
          y,
          scale,
        };
      });
      if (coins.every((c) => c.canvas && c.body)) {
        // Sized by the body's HEIGHT: the tier word sticks out sideways by a
        // different amount per coin, and the faint glow is not the coin.
        // const unit = Math.max(...coins.map((c) => c.canvas.width));
        // const unit = coins[0].canvas.height;
        const unit = coins[0].body.height;
        const rects = coins.map((c) => {
          // const w = c.canvas.width * c.scale;
          // const h = c.canvas.height * c.scale;
          // All a bit bigger in the same space — the gaps close, the ratios
          // stay (Vladimir, 2026-09-30: "they should all be bigger").
          const k = (unit * c.scale * JACKPOT_COIN_SIZE_BOOST) / c.body.height;
          const h = c.canvas.height * k;
          const w = c.canvas.width * k;
          return { ...c, w, h, cx: c.x * unit, cy: c.y * unit };
        });
        const minX = Math.min(...rects.map((r) => r.cx - r.w / 2));
        const maxX = Math.max(...rects.map((r) => r.cx + r.w / 2));
        const minY = Math.min(...rects.map((r) => r.cy - r.h / 2));
        const maxY = Math.max(...rects.map((r) => r.cy + r.h / 2));
        const out = document.createElement("canvas");
        out.width = Math.ceil(maxX - minX);
        out.height = Math.ceil(maxY - minY);
        const ctx = out.getContext("2d");
        rects.forEach((r) =>
          ctx.drawImage(r.canvas, r.cx - r.w / 2 - minX, r.cy - r.h / 2 - minY, r.w, r.h),
        );
        url = out.toDataURL("image/png");
      }
    } catch {
      url = null;
    }
    self._jackpotCompositeURL = url;
    return url;
  }

  /**
   * One texture for one skin of one spine, cached.
   *
   * extractAll() only walks symbolData, so it cannot reach a spine that has no
   * API symbol id — `gamble_level_symbol` is exactly that: a render state, not
   * a reel symbol, the same as `bl` / `bl2`. Its three colour skins still need
   * flat textures so a strip can spin them the way the reels spin symbols.
   *
   * @param {string} spineName  Registered spine asset name.
   * @param {string} skinName   Skin to pose before capture (e.g. "color/low").
   * @param {string[]} [poseAnimations] Extra clips to hold at their LAST frame
   *        before capture — see _extractSingleFrame. Part of the cache key, so
   *        two poses of the same skin are two textures.
   * @returns {PIXI.RenderTexture|null}
   */
  static getSkinTexture(spineName, skinName, poseAnimations = null) {
    const self = SpineTextureExtractor._instance;
    if (!self) return null;
    const poses = Array.isArray(poseAnimations) ? poseAnimations : [];
    const key = `${spineName}|${skinName}|${poses.join(",")}`;
    if (!self._skinTextureCache) self._skinTextureCache = {};
    if (self._skinTextureCache[key] !== undefined) {
      return self._skinTextureCache[key];
    }
    const texture = self._extractSingleFrame(spineName, skinName, poses);
    self._skinTextureCache[key] = texture;
    return texture;
  }

  /**
   * The settled gamble scatter, as ONE flat picture.
   *
   * ── What this replaces ──
   *
   * The cell used to keep the bare card (symbol 18/19/20) with the spin count
   * drawn on top as a separate PIXI.Text, positioned by reproducing the
   * skeleton's `fs_number` bone chain by hand. Three pieces of arithmetic kept
   * in step with a rig they were not part of, and the result still had no gold
   * window around it. That path is gone — see toStaticCell.
   *
   * ── What it does instead ──
   *
   * It builds the LIVE reveal and photographs it. Not an approximation of it —
   * the same objects, in the same order, that RevealSymbolController.showSettled
   * puts on screen:
   *
   *   scg posed at `revealed_static`   the open, decided window
   *   holder -> fit                    the two containers addSlotObject needs
   *   GambleLevelOverlay in `fit`      the card: skin, pips lit, number in the
   *                                    skeleton's own text slot
   *   addSlotObject(attachment_slot_symbol, holder)
   *
   * Nothing is placed by eye and nothing is reproduced. If the rig is
   * re-exported, this capture moves with it exactly as the live card does.
   *
   * `revealed_static` is zero-length and must be LOOPED — a one-shot ends on
   * the frame it starts and Spine reverts to the setup pose, which for this
   * skeleton is the doors SHUT over the card.
   *
   * Cached per (tier, value): three tiers with a handful of counts each, built
   * on demand rather than at load, because the count is not known until the
   * round is answered.
   *
   * ── Two states per card, both real ──
   *
   * `pips` is part of the key because the entry flourish SPENDS the card's
   * pips into the level boxes. Lit is what the player wins; spent is what is
   * left standing on the reel afterwards, and that is the one the next spin
   * carries away. One texture cannot be both.
   *
   * @param   {{tier:number, value:number}} item The landed package.
   * @param   {Function} makeCard  Builds the card into a parent container —
   *   pass GambleLevelOverlay's constructor. Injected rather than imported so
   *   the extractor keeps knowing nothing about the gamble feature.
   * @param   {number|null} [pips] Lit pip count; null means the tier's own.
   * @returns {PIXI.RenderTexture|null}
   */
  /**
   * How many texture pixels one CELL pixel is worth, for the composites.
   *
   * ── The blur this exists for ──
   *
   * A composite is captured at the skeleton's NATIVE size — 194x194 — because
   * the reel scales every symbol by `symbolHeight / Settings.SYMBOL_HEIGHT` and
   * reads nothing from the texture. So the logical size must stay 194 and the
   * only thing that can follow the viewport is the DENSITY.
   *
   * Without it the two ways a Golden coin is drawn disagree. The spinning strip
   * rasterises its number live, at `cellHeight x COIN_VALUE_FONT_SIZE_MULTIPLIER`
   * screen pixels, so it is sharp at any size. The landed cell has that number
   * BAKED into a 194-pixel picture — and above ULTRA_WIDE.BREAKPOINT the cell
   * grows to `ULTRA_WIDE.MAX_SCALE` (2.0), so the picture is magnified 2x while
   * the strip's is not. That is the blur, and it appears on RESIZE because
   * resizing is what changes the scale.
   *
   * Never below 1: a cell smaller than native is a DOWNSAMPLE, which is sharp
   * already, and capturing thinner than today would be a regression on every
   * ordinary screen. Bucketed to halves so dragging a window edge cannot mint a
   * new set of textures per frame — three buckets at most (1, 1.5, 2).
   */
  static _compositeDensity = 1;

  /**
   * Point the composites at the live cell size. Call it before warming them.
   *
   * @param {number} cellHeight The reel's CURRENT symbol height, in pixels.
   * @returns {boolean} whether the bucket changed — the caller re-warms if so.
   */
  static setCompositeDensity(cellHeight) {
    const raw = (cellHeight || 0) / Settings.SYMBOL_HEIGHT;
    if (!(raw > 0)) return false;
    const bucket = Math.min(2, Math.max(1, Math.round(raw * 2) / 2));
    if (bucket === SpineTextureExtractor._compositeDensity) return false;
    SpineTextureExtractor._compositeDensity = bucket;
    return true;
  }

  static getRevealedCardTexture(item, makeCard, pips = null) {
    const self = SpineTextureExtractor._instance;
    if (!self || !item || typeof makeCard !== "function") return null;

    // The density is part of the key: the same card at two viewport sizes is
    // two different pictures, and the wrong one is the blurry one.
    const key = `revealed|${item.tier}|${item.value}|${pips ?? "tier"}|d${SpineTextureExtractor._compositeDensity}`;
    if (!self._skinTextureCache) self._skinTextureCache = {};
    if (self._skinTextureCache[key] !== undefined) {
      return self._skinTextureCache[key];
    }

    const texture = self._extractRevealedCard(item, makeCard, pips, GAMBLE_HOST);
    // ── A FAILURE IS NOT CACHED ──
    //
    // Caching null poisons the whole session: every later call returns the
    // cached null and the cell keeps the closed scatter until the page is
    // reloaded. The only plausible failures here are boot-order ones — a spine
    // not in the Assets cache yet — which cure themselves a moment later, so a
    // retry is exactly the right answer. A capture costs one render.
    if (texture) self._skinTextureCache[key] = texture;
    return texture;
  }

  /**
   * A settled Golden Mystery cell, as ONE flat picture.
   *
   * The third composite, and the same trick as the other two: the mystery frame
   * posed at `revealed_static` — doors open, question mark gone — with whatever
   * the track landed on standing in its window. A coin with its value printed on
   * it, a multiplier, a collector.
   *
   * ── Why the cell needs one ──
   *
   * The feature runs on live spines, and they are handed back the moment the
   * next spin starts. Underneath them the reel still holds the MASKED mystery
   * texture, because a Golden cell is deliberately never given a new identity —
   * so without this the board flashes back to a row of question marks and spins
   * that away. With it, the cell the player is looking at is the cell that
   * travels up and off the screen.
   *
   * ── Keyed by what is DRAWN, not by what it is ──
   *
   * The value is printed into the picture, and what that string says depends on
   * the coin's value and possibly on the stake. So the cache key carries the
   * rendered text: two cells that look identical share a texture and two that do
   * not, cannot.
   *
   * @param   {{symbolId:number,value:number}} item  What the track landed on.
   * @param   {string}   label     The exact string drawn on it — part of the key.
   * @param   {Function} makeCell  Builds that cell into a parent. Pass the SAME
   *   factory the live track uses, or the flat cell and the live one are two
   *   different pictures.
   * @returns {PIXI.RenderTexture|null}
   */
  static getGoldenCellTexture(item, label, makeCell) {
    const self = SpineTextureExtractor._instance;
    if (!self || !item || typeof makeCell !== "function") return null;

    // Density in the key — see setCompositeDensity.
    const key = `golden|${item.symbolId}|${item.value}|${label ?? ""}|d${SpineTextureExtractor._compositeDensity}`;
    if (!self._skinTextureCache) self._skinTextureCache = {};
    if (self._skinTextureCache[key] !== undefined) {
      return self._skinTextureCache[key];
    }

    // Failures are NOT cached — same reason as getRevealedCardTexture.
    const texture = self._extractRevealedCard(item, makeCell, null, MYSTERY_HOST);
    if (texture) self._skinTextureCache[key] = texture;
    return texture;
  }

  constructor(renderer) {
    this.renderer = renderer;
    this._textureCache = null;
    this._dataURLCache = null;
    this._blurredTextureCache = null;
    this._skinTextureCache = null;
  }

  /**
   * Extract normal (sharp) textures for all symbols.
   * Iterates symbolData, deduplicates shared spines, and produces one
   * texture per unique symbol (including per-skin variants like l1–l6).
   * @returns {{ textures: Record<string, PIXI.RenderTexture> }}
   */
  extractAll() {
    if (this._textureCache) return this._textureCache;

    const textures = {};
    const processed = new Set();

    for (const [, symbolInfo] of Object.entries(symbolData)) {
      if (
        !symbolInfo?.spine ||
        symbolInfo.spine === "" ||
        typeof symbolInfo.spine !== "string"
      ) {
        continue;
      }

      const textureName = symbolInfo.texture;
      if (!textureName || processed.has(textureName)) {
        continue;
      }

      const texture = this._extractSingleFrame(
        symbolInfo.spine,
        symbolInfo.skin || null,
        symbolInfo.capturePose || null,
      );

      if (texture) {
        textures[`${textureName}.png`] = texture;
        processed.add(textureName);
      }
    }

    this._textureCache = { textures };
    return this._textureCache;
  }

  /**
   * Convert extracted textures to base64 data URLs for HTML <img> tags
   * (used in paytable and game rules screens).
   * @returns {Record<string, { src: string, width: number, height: number }>}
   */
  extractAllAsDataURLs() {
    if (this._dataURLCache) return this._dataURLCache;

    const spritesheet = this.extractAll();
    if (!spritesheet?.textures) return {};

    const result = {};
    for (const [key, texture] of Object.entries(spritesheet.textures)) {
      const name = key.replace(/\.png$/, "");
      try {
        const canvas = this.renderer.extract.canvas(texture);
        if (canvas) {
          result[name] = {
            src: canvas.toDataURL("image/png"),
            width: canvas.width,
            height: canvas.height,
          };
        }
      } catch {
        // Skip failed extractions
      }
    }

    this._dataURLCache = result;
    return result;
  }

  /**
   * Generate blurred variants of all symbol textures.
   * Takes each normal texture from extractAll(), applies a vertical squash
   * and Gaussian blur (configured via FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION),
   * producing textures used during the spin motion effect.
   * @returns {{ textures: Record<string, PIXI.RenderTexture> }}
   */
  extractAllBlurred() {
    if (this._blurredTextureCache) return this._blurredTextureCache;

    const normalSpritesheet = this.extractAll();
    if (!normalSpritesheet?.textures) return null;

    const textures = {};

    for (const [key, normalTexture] of Object.entries(
      normalSpritesheet.textures,
    )) {
      const blurredKey = key.replace(".png", "_blurred.png");

      try {
        const blurredTexture = this._createBlurredTexture(normalTexture);
        if (blurredTexture) {
          textures[blurredKey] = blurredTexture;
        }
      } catch {
        // Skip failed extractions
      }
    }

    this._blurredTextureCache = { textures };
    return this._blurredTextureCache;
  }

  /**
   * Render a single spine's "static" animation into a RenderTexture.
   *
   * The texture is origin-centered: the spine's design origin (0,0) is placed
   * at the exact center of the texture. This ensures that when a PIXI.Sprite
   * uses anchor(0.5), it aligns pixel-perfectly with the live spine overlay
   * during win animations — eliminating the visual "bounce" that occurs when
   * the static texture's center differs from the spine's origin.
   *
   * @param {string} spineName - Spine asset name (e.g., "lp", "h1", "w1")
   * @param {string|null} skinName - Optional skin to apply (e.g., "l1" for shared spines)
   * @returns {PIXI.RenderTexture|null}
   */
  /**
   * @param {string}   spineName
   * @param {string}   [skinName]
   * @param {string[]} [poseAnimations] Clips to hold at their FINAL frame on
   *        tracks of their own, on top of the base pose. See where they are
   *        applied for why the final frame and not frame 0.
   */
  /**
   * Build the settled reveal and render it. See getRevealedCardTexture.
   *
   * @param   {*}        item     Whatever `makeCard` expects — a package for
   *          the gamble host, a symbol id for the mystery one.
   * @param   {Function} makeCard
   * @param   {number|null} [pips]
   * @param   {{spine:string,slot:string,pose:string}} [hostCfg] Which window
   *          this composite is captured inside. Defaults to the gamble one.
   * @returns {PIXI.RenderTexture|null}
   */
  _extractRevealedCard(item, makeCard, pips = null, hostCfg = GAMBLE_HOST) {
    const assets = PIXI.Assets.cache;
    if (
      !assets.has(`${hostCfg.spine}-skeleton`) ||
      !assets.has(`${hostCfg.spine}-atlas`)
    ) {
      return null;
    }

    let host = null;
    let card = null;
    try {
      host = Spine.from({
        skeleton: `${hostCfg.spine}-skeleton`,
        atlas: `${hostCfg.spine}-atlas`,
        autoUpdate: false,
      });
      if (!host) return null;

      // The two containers addSlotObject needs, exactly as the live path
      // builds them: the holder is the slot's to move, the fit is ours.
      const holder = new PIXI.Container();
      const fit = new PIXI.Container();
      holder.addChild(fit);

      // Native size, so the capture is at scale 1 — the same rule every other
      // extracted texture follows, and what lets the flat cell and the live
      // card be the same pixels.
      card = makeCard({
        parent: fit,
        item,
        cellWidth: Settings.SYMBOL_WIDTH,
        cellHeight: Settings.SYMBOL_HEIGHT,
        pips,
      });
      // ── An empty window is a FAILURE, not a picture ──
      //
      // GambleLevelOverlay._build swallows a Spine.from throw: it warns and
      // leaves `spine` null. The capture would then happily render the open
      // gold window with NOTHING inside it and cache that as a valid texture —
      // worse than returning null, because nothing downstream can tell it is
      // wrong. So the card having SOMETHING to draw is the precondition.
      //
      // A skeleton or a container: the gamble card and the revealed symbol are
      // spines, a settled Golden cell is a sprite with a value printed on it.
      // The check is that the factory produced something, not what kind.
      if (!card?.spine && !card?.container) {
        console.warn(
          "[SpineTextureExtractor] Composite card is empty — capture skipped.",
        );
        return null;
      }
      // Nothing may tick during a capture.
      if (card.spine) card.spine.autoUpdate = false;

      host.addSlotObject(hostCfg.slot, holder);

      // LOOPED — see the note on getRevealedCardTexture.
      host.state.setAnimation(0, hostCfg.pose, true);
      host.state.update(0);
      host.state.apply(host.skeleton);

      // ── THE POSE HAS TO REACH THE BONES BEFORE THEIR SCALE IS READ ──
      //
      // `bone.a` and `bone.c` are the first column of the bone's WORLD matrix,
      // and only `skeleton.updateWorldTransform()` writes that — which is what
      // Spine.update does, and `state.apply()` above does not. So the fit was
      // being computed from whatever world transform the skeleton was
      // constructed with, the SETUP pose, while `win` is the pose actually
      // being captured.
      //
      // RevealSymbolController never had this problem: its host is a live,
      // ticking spine, so by the time _applyFit runs the world transform is
      // the pose's own. The capture is the one place the order has to be
      // stated, and stating it wrong made the card inside the window come out
      // bigger than the one that lands live. Vladimir, 2026-09-23: *"it
      // shrinks but not properly to fit the spines bounds"*.
      host.update(0);

      // _applyFit, from RevealSymbolController. The holder inherits the
      // placeholder bone's world scale (the 1.3 `scale` bone every symbol
      // skeleton here is built under); the host's own PIXI scale is 1 at
      // capture. Dividing it out leaves one unit under `fit` equal to one
      // cell pixel — the unit the card sizes itself in.
      const bone = host.skeleton?.findSlot?.(hostCfg.slot)?.bone;
      const boneScale = bone ? Math.hypot(bone.a, bone.c) : 1;
      fit.scale.set(boneScale ? 1 / boneScale : 1);

      // ── The CARD gets an update too, for the reason the host does ──
      //
      // Its spin count is a SLOT OBJECT (GambleLevelOverlay does
      // `spine.addSlotObject(txt_slot_number_*, holder)`), and slot objects are
      // placed during the Spine's own update. `autoUpdate` is off above so
      // nothing ticks during a capture, which left the card never updated at
      // all — the host was, the card was not.
      //
      // Added 2026-09-23. It did not turn out to be the bug being chased (that
      // was an overlay spine covering the finished cell, see useWinController),
      // and the capture looked the same either way at the resolution it was
      // compared at. Kept because it is the documented rule for slot objects
      // and the line below states it for the host; a composite that has to
      // place one should not be relying on luck.
      if (card.spine) card.spine.update(0);

      // Slot objects are placed during the Spine's own update, which is off.
      host.update(0);

      return this._renderCentered(host, SpineTextureExtractor._compositeDensity);
    } catch (error) {
      console.warn("[SpineTextureExtractor] Revealed card capture failed:", error);
      return null;
    } finally {
      card?.destroy?.();
      if (host && !host.destroyed) {
        host.state?.clearTracks?.();
        host.destroy({ children: true });
      }
    }
  }

  /**
   * Render a display object into an origin-centred RenderTexture.
   *
   * Lifted out of _extractSingleFrame unchanged so the composite capture and
   * the per-symbol one cannot drift: both must put the spine's (0,0) at the
   * texture centre, or a sprite with anchor 0.5 will not line up with the live
   * spine it replaces.
   *
   * @param   {PIXI.DisplayObject} target
   * @returns {PIXI.RenderTexture|null}
   */
  _renderCentered(target, densityMul = 1) {
    const bounds = target.getLocalBounds();
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) return null;

    const halfW = Math.max(Math.abs(bounds.x), Math.abs(bounds.x + bounds.width));
    const halfH = Math.max(Math.abs(bounds.y), Math.abs(bounds.y + bounds.height));
    const texWidth = Math.ceil(halfW * 2) + 16;
    const texHeight = Math.ceil(halfH * 2) + 16;

    const renderTexture = PIXI.RenderTexture.create({
      width: texWidth,
      height: texHeight,
      // SIZE stays native and only the density moves — the reel scales this
      // sprite by `symbolHeight / Settings.SYMBOL_HEIGHT` and never looks at
      // the texture, so a bigger texture would draw a bigger cell. More pixels
      // inside the same 194 is the only lever. See setCompositeDensity.
      resolution: this.renderer.resolution * densityMul,
    });

    const prevX = target.position.x;
    const prevY = target.position.y;
    target.position.set(texWidth / 2, texHeight / 2);
    this.renderer.render(target, { renderTexture });
    target.position.set(prevX, prevY);

    return renderTexture;
  }

  _extractSingleFrame(spineName, skinName = null, poseAnimations = null) {
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
      return null;
    }

    let spine = null;
    try {
      spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        autoUpdate: false,
      });

      // Apply skin for shared spines (e.g., "lp" spine → skins "l1"–"l6")
      if (
        FEATURES_CONFIG.SPINE_ANIMATIONS.SHARED_SPINE_SKINS &&
        skinName &&
        spine.skeleton
      ) {
        spine.skeleton.setSkinByName(skinName);
        spine.skeleton.setSlotsToSetupPose();
      }

      // Select the best animation for frame-0 extraction.
      // Priority: idle → loop → win → bounce → first available.
      //
      // `bounce` is the landing animation, so its frame 0 is the symbol in its
      // closed, just-landed state — the correct look for a spinning reel.
      // It is last because the symbols that have `idle` or `win` already
      // extract correctly and must not change.
      //
      // Without it the fallback is `animations[0]`, which is whatever the
      // exporter happened to write first. That gave `scg` its `anticipation`
      // frame — doors open, additive yellow rectangle at full alpha — so the
      // gamble scatter spun past looking already revealed and only turned
      // golden once the live spine took over. `my` and `cl` had the same fault
      // for the same reason.
      //
      // `static` FIRST. It is exported as an empty animation ({} with no
      // timelines), and that is the point rather than a defect: applying no
      // timelines leaves the skeleton in its SETUP POSE, which is the symbol's
      // resting look and exactly the frame a spinning texture should show.
      //
      // An earlier version of this list left `static` out, on the reading that
      // an empty animation "fixes nothing". That was wrong. The case it was
      // meant to solve — `scg` capturing a full-alpha yellow rectangle — comes
      // from `anticipation` frame 0, which ASSIGNS that attachment. At setup
      // the same slot has no attachment at all and draws nothing.
      //
      // `in` FIRST (Cleopatra's Fire, 2026-09-29). It is the landing — the
      // first clip the live spine plays when the reel stops — so a texture
      // taken from its frame 0 hands over to the spine with no jump. Vladimir:
      // "connect texture to the first spine in a row". Every symbol but `l`
      // (win only) and `bl` has one; those two fall through as before.
      // const ANIM_PRIORITY = ["static", "idle", "loop", "win", "bounce"];
      const ANIM_PRIORITY = ["in", "static", "idle", "loop", "win", "bounce"];
      const animations = spine.skeleton?.data?.animations;
      let selectedAnim = null;

      // ── Some rigs must be captured at SETUP, whatever they ship ──
      //
      // The list above picks the first clip a rig happens to have, which is a
      // guess that holds only while `idle` means "the resting look". On
      // `gamble_cards` it does not: its `idle` CLEARS the attachment on all
      // sixteen rat slots at frame 0, because that clip is the card's glow loop
      // and is authored to play on a card, not on a rat. Capturing it gives the
      // `fail` skin an empty texture, and every rat cell on the gamble belt
      // draws nothing.
      //
      // The file used to carry an empty `static` clip to win the priority list,
      // added by hand — and an animator re-export deleted it twice. So the
      // answer lives HERE instead, where a re-export cannot reach it: named
      // rigs skip the list and pose at setup, which is what an empty `static`
      // was standing in for anyway.
      const useSetupPose = SETUP_POSE_SPINES.has(spineName);

      if (!useSetupPose && animations?.length) {
        for (const preferred of ANIM_PRIORITY) {
          selectedAnim = animations.find(
            (a) => a.name.toLowerCase() === preferred,
          );
          if (selectedAnim) break;
        }
        // Ultimate fallback: use the first animation available
        if (!selectedAnim) {
          selectedAnim = animations[0];
        }
      }

      if (!selectedAnim && !useSetupPose) {
        console.warn(
          `[SpineTextureExtractor] Spine "${spineName}" has no animations — skipping extraction.`,
        );
        spine.destroy({ children: true });
        return null;
      }

      // Capture at t=0 — the overlay spine also starts with update(0), so both
      // show the exact same pose. Using selectedAnim.duration previously caused
      // subtle differences if the idle animation doesn't loop back perfectly.
      // Left untouched on the setup-pose path — not setting a track IS the
      // pose, and it is what an empty `static` clip produced.
      if (selectedAnim) {
        spine.state.setAnimation(0, selectedAnim.name, false);
      }

      // ── Extra clips, held at their LAST frame ──
      //
      // The base pose above is captured at t=0, which is right for it: it is
      // the symbol at rest. These are the opposite — they are clips whose
      // POINT is their end state. The gamble card's level pips are the case
      // this exists for: `level_indicator/levelN/on` animates a dot from dark
      // to lit, so frame 0 is a dark dot and captures a card that looks
      // unearned. The player must see the pips already lit while the strip is
      // still spinning, so the capture has to be of the finished pose.
      //
      // A track each, because a track holds one animation and these would
      // otherwise replace each other — leaving exactly one dot lit. Safe
      // because the clips are disjoint: each keys only its own slots.
      //
      // trackTime is set directly rather than advanced, and trackEnd pushed
      // out, or the entry is disposed during update and the skeleton snaps
      // back to a setup pose that has no attachment on those slots at all.
      if (Array.isArray(poseAnimations) && poseAnimations.length) {
        poseAnimations.forEach((name, i) => {
          const clip = animations?.find((a) => a.name === name);
          if (!clip) {
            console.warn(
              `[SpineTextureExtractor] "${spineName}" has no clip "${name}" to pose.`,
            );
            return;
          }
          const entry = spine.state.setAnimation(i + 1, clip.name, false);
          if (!entry) return;
          entry.trackEnd = Number.MAX_VALUE;
          entry.trackTime = clip.duration;
        });
      }

      spine.state.update(0);
      spine.state.apply(spine.skeleton);

      try {
        if (spine.skeleton.physics !== undefined) {
          spine.skeleton.updateWorldTransform(2);
        } else {
          spine.skeleton.updateWorldTransform(0);
        }
      } catch {
        try {
          spine.skeleton.updateWorldTransform();
        } catch {}
      }

      spine.alpha = 1;

      const bounds = spine.getLocalBounds();
      if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
        spine.destroy({ children: true });
        return null;
      }

      // Origin-centered canvas: size the texture so spine origin (0,0) is at center.
      // This produces a symmetric canvas that aligns with sprite anchor(0.5).
      // +2 safety margin: asymmetric bounds (e.g. -93 to +94) cause the content
      // edge to land exactly at the canvas boundary, clipping 1px.
      const halfW = Math.max(
        Math.abs(bounds.x),
        Math.abs(bounds.x + bounds.width),
      );
      const halfH = Math.max(
        Math.abs(bounds.y),
        Math.abs(bounds.y + bounds.height),
      );
      const texWidth = Math.ceil(halfW * 2) + 16;
      const texHeight = Math.ceil(halfH * 2) + 16;

      const renderTexture = PIXI.RenderTexture.create({
        width: texWidth,
        height: texHeight,
        resolution: this.renderer.resolution,
      });

      // Origin at canvas center — matches sprite anchor(0.5)
      // +1 accounts for the safety margin added to canvas dimensions
      spine.position.set(texWidth / 2, texHeight / 2);
      this.renderer.render(spine, { renderTexture });

      spine.state.clearTracks();
      spine.destroy({ children: true });
      spine = null;

      return renderTexture;
    } catch (error) {
      console.warn(
        `[SpineTextureExtractor] Failed to extract "${spineName}":`,
        error,
      );
      if (spine) {
        try {
          spine.destroy({ children: true });
        } catch {}
      }
      return null;
    }
  }

  /**
   * Post-process a texture into the blurred variant the spin swaps to.
   *
   * ── Why this is not a blur filter any more ──
   *
   * It used to be `PIXI.BlurFilter` at strength 26, quality 4. That filter is
   * not a true gaussian: it is a 5-tap kernel run `quality` times, and the
   * taps sit `strength / quality` texels apart — 6.5px at those settings. So
   * the output was not a smear at all, it was six-and-a-half-pixel ghost
   * copies of the symbol stacked up the cell. Those were the horizontal lines
   * inside every blurred symbol on a 6-row board.
   *
   * No combination of that filter's own knobs fixes it. Smoothness needs a tap
   * spacing near one texel, spacing is strength/quality, and holding the blur
   * amount while raising quality means raising strength too — so the spacing
   * only improves with the SQUARE ROOT of the passes. Getting to one texel at
   * this strength wanted about 26 passes per symbol.
   *
   * What the config always said it wanted was "PS motion blur 90°/30px", and a
   * Photoshop motion blur is a box average along a line — so that is what this
   * draws now: N copies of the symbol, evenly spaced along the travel, each at
   * 1/N alpha, summed. Smooth by construction at any strength, closer to the
   * original artistic intent than the gaussian ever was, and CHEAPER: the
   * copies share one texture and one blend mode, so PIXI batches them into a
   * single draw call, against the filter's four full passes.
   *
   * Boot cost only. Nothing here runs during a spin.
   *
   * @param {PIXI.RenderTexture} sourceTexture - Normal extracted texture
   * @returns {PIXI.RenderTexture|null}
   */
  _createBlurredTexture(sourceTexture) {
    if (
      !sourceTexture ||
      sourceTexture.width <= 0 ||
      sourceTexture.height <= 0
    ) {
      return null;
    }

    const config = FEATURES_CONFIG.SPIN.BLURRED_TEXTURE_GENERATION;
    const texWidth = sourceTexture.width;
    const texHeight = sourceTexture.height;

    const scaleX = config.SCALE_X ?? 1.0;

    // How far the symbol travels during the exposure — a SHARE of the cell,
    // never a pixel count. The extracted texture is a different size on every
    // device, so a fixed number is a different smear on each of them.
    //
    // THE ONLY KNOB. Everything below is derived from it.
    const ratio = config.SMEAR_RATIO ?? 0.21;

    // ── The squash is derived, not configured ──
    //
    // It only ever existed to make room for the smear inside a texture that
    // stays the source's size, so having it as its own number meant two knobs
    // fighting each other: raise the smear and it clipped, raise the squash
    // and the symbol lost height for nothing. Worse, they were set
    // independently, so the old pair squashed the symbol by 25% and then used
    // only part of the room that bought.
    //
    // Now the symbol is left as TALL AS IT CAN BE while its own smear still
    // fits. Set the smear; the height follows.
    const contentH = Math.max(1, texHeight - EXTRACT_PADDING_PX);
    // Cap the smear at what the squash floor can make room for, so an extreme
    // ratio shortens the smear rather than crushing the symbol.
    const maxSmear = Math.max(0, texHeight - contentH * MIN_SMEAR_SCALE_Y);
    const smear = Math.max(0, Math.min(texHeight * ratio, maxSmear));
    // Exactly enough squash to fit, and never a stretch: when the smear is
    // small enough to live inside the extractor's own padding, the symbol is
    // left at full height.
    const scaleY = Math.min(1, (texHeight - smear) / contentH);

    // One sample per pixel of travel is exactly where the copies start to
    // overlap and the smear becomes continuous. Below that it is a row of
    // ghosts; above it there is nothing left to gain.
    const samples = Math.max(
      1,
      Math.min(MAX_SMEAR_SAMPLES, Math.ceil(smear) || 1),
    );

    const group = new PIXI.Container();
    for (let i = 0; i < samples; i += 1) {
      const sprite = new PIXI.Sprite(sourceTexture);
      sprite.anchor.set(0.5);
      sprite.scale.set(scaleX, scaleY);
      // Equal weights — a box average along the path, which is what motion
      // blur is. Uneven weights would make a comet, not a moving symbol.
      sprite.alpha = 1 / samples;
      // Textures here are premultiplied, and ADD is ONE/ONE, so the copies SUM
      // rather than compositing over one another. At 1/N each that sum is
      // their mean: a real average of the exposure, not N ghosts fading out.
      sprite.blendMode = PIXI.BLEND_MODES.ADD;
      const t = samples === 1 ? 0.5 : i / (samples - 1);
      sprite.position.set(texWidth / 2, texHeight / 2 + (t - 0.5) * smear);
      group.addChild(sprite);
    }

    const renderTexture = PIXI.RenderTexture.create({
      width: texWidth,
      height: texHeight,
      // Deliberately 1, NOT the renderer's resolution.
      //
      // The sharp set is built at 2x on retina and this one is not, so it is
      // upscaled in the cell — and that looks like the fault, but it is not.
      // Matching the renderer here quadruples this set's GPU memory and was
      // measured to hurt phones badly. A smooth gradient survives an upscale
      // almost for free; the stepped output above did not, which is the whole
      // reason the density looked like the problem.
      resolution: 1,
    });

    this.renderer.render(group, { renderTexture });

    // Children only — the source texture is shared and must outlive this.
    group.destroy({ children: true });

    return renderTexture;
  }

  /** Free all GPU memory held by extracted textures. */
  dispose() {
    if (this._textureCache?.textures) {
      for (const texture of Object.values(this._textureCache.textures)) {
        if (texture && !texture.destroyed) {
          texture.destroy(true);
        }
      }
      this._textureCache = null;
    }

    if (this._blurredTextureCache?.textures) {
      for (const texture of Object.values(this._blurredTextureCache.textures)) {
        if (texture && !texture.destroyed) {
          texture.destroy(true);
        }
      }
      this._blurredTextureCache = null;
    }

    this._trimmedDataURLCache = null;
    this._jackpotCompositeURL = undefined;
    if (this._skinTextureCache) {
      for (const texture of Object.values(this._skinTextureCache)) {
        if (texture && !texture.destroyed) {
          texture.destroy(true);
        }
      }
      this._skinTextureCache = null;
    }
  }
}

export default SpineTextureExtractor;
